import { Helmet } from "react-helmet-async";
import {
  BellRing,
  MessageCircle,
  Users2,
  Share2,
  Clock,
  Sparkles,
} from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import Stats from "../components/homepage/Stats";
import PageHero, {
  SectionHeader,
  FeatureGrid,
  NumberedStrip,
} from "../components/shared/PageHero";

const PILLARS = [
  { icon: BellRing, title: "Push Notifications", text: "Behavioral, cohorted, perfectly-timed pushes. Not spam. Real lift in D7 and D30 retention." },
  { icon: MessageCircle, title: "In-App Messaging", text: "Contextual nudges that feel like part of the product — not a pop-up." },
  { icon: Users2, title: "Onboarding Optimization", text: "Cut drop-off between install and first Aha moment by 30–60%." },
  { icon: Share2, title: "Referral Programs", text: "K-factor engines that turn your best users into your best growth channel." },
  { icon: Clock, title: "Lifecycle Orchestration", text: "Every touchpoint — day 0 to day 180 — mapped, triggered, measured." },
  { icon: Sparkles, title: "Win-Back Campaigns", text: "Resurrect lapsed users with email + push + in-app + paid re-engagement." },
];

const PROCESS = [
  { title: "Cohort Audit", text: "Map D1/D7/D30 retention by source, OS, market and persona." },
  { title: "Moment Map", text: "Identify every drop-off point and the fix that unlocks each." },
  { title: "Build", text: "Implement push, in-app, email + referral — orchestrated, not stacked." },
  { title: "Test", text: "A/B everything: copy, timing, cadence, channel." },
  { title: "Scale", text: "Roll winners across markets. Monitor for notification fatigue." },
];

export default function AppRetentionPage() {
  return (
    <>
      <Helmet>
        <title>
          App Retention & Engagement Services — Push, In-App, Onboarding | Lumotis
        </title>
        <meta
          name="description"
          content="Lumotis's App Retention service combines push notifications, in-app messaging, onboarding optimization and referral programs to keep users active beyond day 30."
        />
        <link rel="canonical" href="https://lumotis.com/app-retention" />
      </Helmet>
      <Navbar />
      <main data-testid="retention-page">
        <PageHero
          testid="retention-hero"
          eyebrow="App Retention"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "App Retention" }]}
          title="Stop leaking users on"
          titleAccent="day 7."
          subtitle="Push, in-app, onboarding, referrals and lifecycle orchestration — so the users your ASO ranks and UA buys actually stick around long enough to pay."
          primaryCta={{ to: "/contact", label: "Audit my retention", testid: "retention-cta-primary" }}
          secondaryCta={{ to: "/#case-studies", label: "See results", testid: "retention-cta-secondary" }}
          rightSlot={
            <div className="bg-charcoal text-white p-8 shadow-2xl shadow-electric/20 rounded-2xl">
              <p className="text-overline text-electric mb-5">Retention lift (avg.)</p>
              <div className="space-y-5">
                {[
                  { k: "D1 Retention", b: "32%", a: "56%" },
                  { k: "D7 Retention", b: "18%", a: "34%" },
                  { k: "D30 Retention", b: "8%", a: "21%" },
                ].map((r) => (
                  <div
                    key={r.k}
                    className="flex items-center justify-between border-b border-white/10 pb-3 last:border-0"
                  >
                    <span className="text-sm text-zinc-300">{r.k}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-500 line-through text-sm">{r.b}</span>
                      <span className="font-display text-2xl font-black text-electric">
                        {r.a}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          }
        />

        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <SectionHeader
              eyebrow="01 — Pillars"
              title="Six levers that move retention."
              description="Engagement isn't one campaign — it's a system."
            />
            <FeatureGrid items={PILLARS} testidPrefix="retention-pillar" />
          </div>
        </section>

        <section className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-50 pointer-events-none" />
          <div className="container relative">
            <div className="max-w-3xl mb-14">
              <p className="text-overline text-electric mb-3">02 — Process</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-balance">
                From leaky funnel to lifecycle engine.
              </h2>
            </div>
            <NumberedStrip steps={PROCESS} testidPrefix="retention-step" />
          </div>
        </section>

        <Stats />
        <CTABanner />
      </main>
      <Footer />
    </>
  );
}
